import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { environment } from '../../../../../apps/tripointe-cms/src/environments/environment';
import { bytesToMb } from '@tripointe/tripointe-cms/utils';

interface ValidationConfig {
  maxSizeMb?: number;
  types?: string[];
  minWidth?: number;
  minHeight?: number;
}

const MIME_TYPE_WITH_SLASH_REGEX = /^([a-z]*\/)/i;

export const FILES_VALIDATION_CONFIG: { [key: string]: ValidationConfig } = {
  headshot: {
    maxSizeMb: 8,
    types: ['image/jpg', 'image/jpeg', 'image/png'],
    minWidth: 64,
    minHeight: 64
  },
  divisionLogo: {
    maxSizeMb: 8,
    types: ['image/svg', 'image/svg+xml']
  },
  phasePreview: {
    maxSizeMb: 8,
    minWidth: 880,
    minHeight: 495,
    types: ['image/jpg', 'image/jpeg', 'image/png']
  },
  elevationRendering: {
    maxSizeMb: 8,
    minWidth: 1280,
    minHeight: 960,
    types: ['image/jpg', 'image/jpeg', 'image/png']
  },
  elevationPhoto: {
    maxSizeMb: 8,
    minWidth: 785,
    minHeight: 524,
    types: ['image/jpg', 'image/jpeg', 'image/png']
  },
  homePhoto: {
    maxSizeMb: 8,
    minWidth: 785,
    minHeight: 524,
    types: ['image/jpg', 'image/jpeg', 'image/png']
  },
  document: {
    maxSizeMb: 8,
    types: ['application/pdf']
  },
  messageAttachment: {
    maxSizeMb: 25
  }
};

@Injectable({
  providedIn: 'root'
})
export class FilesHelperService {
  constructor(private http: HttpClient) {}

  uploadHeadshot(blob: Blob, fileName: string): any {
    const payload = this.blobToFormData(blob, fileName);

    return this.http.post(`${environment.apiUrl}/users/upload-avatar`, payload);
  }

  uploadFile(blob: Blob, fileName: string, uploadPath: string): any {
    const payload = this.blobToFormData(blob, fileName);

    return this.http.post(`${environment.apiUrl}/${uploadPath}`, payload);
  }

  blobToFormData(blobFile: Blob, fileName: string): FormData {
    const file = new File([blobFile], fileName);
    const formData = new FormData();
    formData.append('file', file);

    return formData;
  }

  getFileDataUrl(file: File): Promise<ArrayBuffer | string> {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    return new Promise(resolve => {
      reader.onload = () => resolve(reader.result);
    });
  }

  validateImage = async (
    file: File,
    validationConfig: ValidationConfig,
    withName = false
  ): Promise<string> => {
    if (validationConfig.types && !validationConfig.types.includes(file.type)) {
      return `Accepted file types: ${this.formatFileTypes(validationConfig.types)} `;
    }

    if (validationConfig.maxSizeMb && bytesToMb(file.size) > validationConfig.maxSizeMb) {
      return `Max file size is ${validationConfig.maxSizeMb} MB`;
    }

    if (validationConfig.minWidth || validationConfig.minHeight) {
      return this.validateImageDimensions(file, validationConfig, withName);
    }

    return null;
  };

  validateImageDimensions = async (
    image: File,
    validationConfig: ValidationConfig,
    withName = false
  ): Promise<string> => {
    const dimensions = await this.getImageDimensions(image);

    if (
      dimensions.width < validationConfig.minWidth ||
      dimensions.height < validationConfig.minHeight
    ) {
      return `${withName ? 'Error with ' + image.name + ' - ' : ''}Min image width should be ${
        validationConfig.minWidth
      }px and min height ${validationConfig.minHeight}px.`;
    }
  };

  formatFileTypes(types: string[]): string {
    return types.map(this.fileTypeToExtension).join(', ');
  }

  fileTypeToExtension(type: string): string {
    return type.replace(MIME_TYPE_WITH_SLASH_REGEX, '').toUpperCase();
  }

  getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise(resolve => {
      const img = new Image();
      img.src = window.URL.createObjectURL(file);

      img.onload = () => {
        const { width, height } = img;
        resolve({ width, height });
      };
    });
  }

  validateFile(file: File, validationConfig: ValidationConfig): string {
    if (validationConfig.types && !validationConfig.types.includes(file.type)) {
      return `Accepted file types: ${this.formatFileTypes(validationConfig.types)} `;
    }

    if (validationConfig.maxSizeMb && bytesToMb(file.size) > validationConfig.maxSizeMb) {
      return `Max file size is ${validationConfig.maxSizeMb} MB`;
    }

    return null;
  }
}
